import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import PremiumLock from "../../components/PremiumLock";
import { getRecipeImage } from "../../utils/recipeImages";
import { 
  ArrowLeft,
  Utensils,
  Clock,
  ChevronDown,
  ChevronUp,
  Info,
  Flame,
  MessageCircle,
  Target,
  RefreshCw,
  Pill,
  FileText,
  AlertTriangle,
  Apple,
  Beef,
  ChefHat,
  Check,
  Calculator,
  Salad
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DietaPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, isPremium } = useAuth();
  
  // Traditional diet state
  const [diet, setDiet] = useState(null);
  const [isAssigned, setIsAssigned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [activeTab, setActiveTab] = useState("comidas");
  
  // Diet mode state (new flexible system)
  const [dietConfig, setDietConfig] = useState(null);
  const [dietMode, setDietMode] = useState("traditional");
  const [flexiblePlan, setFlexiblePlan] = useState(null);
  const [todayMeals, setTodayMeals] = useState(null);
  
  // Recipe selection state
  const [selectedMealSlot, setSelectedMealSlot] = useState(null);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [showRecipeDialog, setShowRecipeDialog] = useState(false);
  const [calculatingRecipe, setCalculatingRecipe] = useState(false);
  const [calculatedRecipe, setCalculatedRecipe] = useState(null);

  // Fetch unified diet config
  const fetchDietConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/premium/my-diet-config`, {
        headers: getAuthHeaders(),
      });
      
      if (response.data.has_config) {
        setDietConfig(response.data);
        setDietMode(response.data.diet_mode || "traditional");
        
        if (response.data.traditional_diet) {
          setDiet(response.data.traditional_diet);
          setIsAssigned(true);
        }
        
        if (response.data.flexible_plan) {
          setFlexiblePlan(response.data.flexible_plan);
        }
      } else {
        // Fallback to old endpoint
        const oldResponse = await axios.get(`${API_URL}/api/premium/diet`, {
          headers: getAuthHeaders(),
        });
        setDiet(oldResponse.data.diet);
        setIsAssigned(oldResponse.data.is_assigned || false);
        setDietMode("traditional");
      }
    } catch (error) {
      console.error("Error fetching diet config:", error);
      // Fallback to old endpoint
      try {
        const oldResponse = await axios.get(`${API_URL}/api/premium/diet`, {
          headers: getAuthHeaders(),
        });
        setDiet(oldResponse.data.diet);
        setIsAssigned(oldResponse.data.is_assigned || false);
        setDietMode("traditional");
      } catch (err) {
        console.error("Error fetching diet:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch today's meals for flexible mode
  const fetchTodayMeals = useCallback(async () => {
    if (dietMode === "traditional") return;
    
    try {
      const response = await axios.get(`${API_URL}/api/premium/today-meals`, {
        headers: getAuthHeaders(),
      });
      setTodayMeals(response.data);
    } catch (error) {
      console.error("Error fetching today meals:", error);
    }
  }, [getAuthHeaders, dietMode]);

  // Fetch recipes for a meal slot
  const fetchRecipesForSlot = async (slotId) => {
    setLoadingRecipes(true);
    try {
      const objetivo = dietConfig?.objetivo || "recomposicion";
      const response = await axios.get(
        `${API_URL}/api/premium/recipes/for-meal/${slotId}/filtered?objetivo=${objetivo}`,
        { headers: getAuthHeaders() }
      );
      setAvailableRecipes(response.data.recipes || []);
    } catch (error) {
      console.error("Error fetching recipes:", error);
      toast.error("Error al cargar recetas");
    } finally {
      setLoadingRecipes(false);
    }
  };

  // Calculate recipe with adjusted ingredients
  const calculateRecipe = async (recipeId, targetMacros) => {
    setCalculatingRecipe(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/premium/recipe-calculator`,
        {
          recipe_id: recipeId,
          target_macros: targetMacros,
          meal_slot: selectedMealSlot
        },
        { headers: getAuthHeaders() }
      );
      setCalculatedRecipe(response.data);
    } catch (error) {
      console.error("Error calculating recipe:", error);
      toast.error("Error al calcular receta");
    } finally {
      setCalculatingRecipe(false);
    }
  };

  // Save recipe selection - includes full recipe data for inline display
  const saveRecipeSelection = async () => {
    if (!calculatedRecipe || !selectedMealSlot) return;
    
    try {
      await axios.post(
        `${API_URL}/api/premium/recipe-selection`,
        {
          meal_slot: selectedMealSlot,
          recipe_id: calculatedRecipe.recipe_id,
          recipe_name: calculatedRecipe.recipe_name,
          adjusted_ingredients: calculatedRecipe.adjusted_ingredients,
          final_macros: calculatedRecipe.final_macros,
          target_macros: calculatedRecipe.target_macros,
          deviation: calculatedRecipe.deviation,
          fit_score: calculatedRecipe.fit_score,
          // Include recipe details for inline display
          preparacion: calculatedRecipe.preparacion,
          tiempo_preparacion: calculatedRecipe.tiempo_preparacion,
          tips_goatgym: calculatedRecipe.tips_goatgym
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Receta guardada para hoy");
      setShowRecipeDialog(false);
      setCalculatedRecipe(null);
      setSelectedMealSlot(null);
      fetchTodayMeals();
    } catch (error) {
      console.error("Error saving selection:", error);
      toast.error("Error al guardar selección");
    }
  };

  useEffect(() => {
    if (isPremium) {
      fetchDietConfig();
    } else {
      setLoading(false);
    }
  }, [isPremium, fetchDietConfig]);

  useEffect(() => {
    if (dietMode !== "traditional" && !loading) {
      fetchTodayMeals();
    }
  }, [dietMode, loading, fetchTodayMeals]);

  const toggleMeal = (index) => {
    setExpandedMeal(expandedMeal === index ? null : index);
  };

  const getMealIcon = (mealName) => {
    const name = (mealName || "").toLowerCase();
    if (name.includes("desayuno")) return "🌅";
    if (name.includes("mañana") || name.includes("media")) return "🍎";
    if (name.includes("comida") || name.includes("almuerzo")) return "🍽️";
    if (name.includes("merienda") || name.includes("pre")) return "🥤";
    if (name.includes("cena") || name.includes("post")) return "🌙";
    return "🍴";
  };

  const openRecipeSelector = (slotId) => {
    setSelectedMealSlot(slotId);
    setCalculatedRecipe(null);
    setShowRecipeDialog(true);
    fetchRecipesForSlot(slotId);
  };

  // FREE user - Show PremiumLock
  if (!isPremium) {
    return (
      <div className="animate-fade-in pb-8" data-testid="dieta-page-locked">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl hover:bg-[#1A1A1A] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#888]" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-black text-white">Mi Dieta</h1>
            <p className="text-[#666] text-sm">Plan de alimentación personalizado</p>
          </div>
        </div>
        <PremiumLock
          title="Disponible en Premium"
          description="Para resultados reales necesitas un plan personalizado"
          className="min-h-[400px]"
        >
          <div className="space-y-3 p-4">
            <div className="h-24 bg-[#1A1A1A] rounded-xl" />
            <div className="h-20 bg-[#1A1A1A] rounded-xl" />
            <div className="h-20 bg-[#1A1A1A] rounded-xl" />
          </div>
        </PremiumLock>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#666] mt-4">Cargando dieta...</p>
      </div>
    );
  }

  // No diet config at all
  const hasAnyDiet = isAssigned || flexiblePlan;
  if (!hasAnyDiet) {
    return (
      <div className="animate-fade-in pb-8" data-testid="dieta-page-pending">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl hover:bg-[#1A1A1A] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#888]" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-black text-white">Mi Dieta</h1>
            <p className="text-[#666] text-sm">Plan de alimentación personalizado</p>
          </div>
        </div>

        <Card className="bg-[#121212] border-[#2A2A2A]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
              <Utensils className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h3 className="text-white text-lg font-bold mb-2">Tu plan nutricional está pendiente</h3>
            <p className="text-[#888] text-sm mb-6 max-w-sm mx-auto">
              Tu plan nutricional está pendiente de asignación por el coach. 
              Pronto recibirás tu dieta adaptada a tus objetivos.
            </p>
            <Button
              onClick={() => window.open("https://wa.me/34641099731?text=Hola!%20Soy%20usuario%20Premium%20y%20me%20gustaría%20saber%20sobre%20mi%20dieta", "_blank")}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Consultar por WhatsApp
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get daily targets
  const dailyTargets = dietConfig?.daily_targets || {
    calories: diet?.calories_target || 0,
    protein: diet?.protein_target || 0,
    carbs: diet?.carbs_target || 0,
    fat: diet?.fat_target || 0
  };

  // ========== RENDER BASED ON DIET MODE ==========
  return (
    <div className="animate-fade-in pb-24" data-testid="dieta-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl hover:bg-[#1A1A1A] transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-[#888]" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-black text-white">
            {dietMode === "traditional" && diet?.name || "Mi Dieta"}
          </h1>
          <div className="flex items-center gap-2">
            <Badge className={cn(
              "border-none text-xs",
              dietMode === "traditional" && "bg-blue-500/10 text-blue-400",
              dietMode === "flexible" && "bg-green-500/10 text-green-400",
              dietMode === "hybrid" && "bg-purple-500/10 text-purple-400"
            )}>
              {dietMode === "traditional" && "Tradicional"}
              {dietMode === "flexible" && "Flexible"}
              {dietMode === "hybrid" && "Híbrido"}
            </Badge>
            <span className="text-[#D4AF37] text-sm font-medium">GOATGYM Pro</span>
          </div>
        </div>
      </div>

      {/* Macros summary */}
      <Card className="bg-[#121212] border-[#2A2A2A] mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-lg font-bold text-white">{dailyTargets.calories}</span>
              </div>
              <p className="text-[10px] text-[#666] uppercase">Calorías</p>
            </div>
            <div>
              <span className="text-lg font-bold text-red-400">{dailyTargets.protein}g</span>
              <p className="text-[10px] text-[#666] uppercase">Proteína</p>
            </div>
            <div>
              <span className="text-lg font-bold text-yellow-400">{dailyTargets.carbs || "~"}g</span>
              <p className="text-[10px] text-[#666] uppercase">Carbos</p>
            </div>
            <div>
              <span className="text-lg font-bold text-blue-400">{dailyTargets.fat || "~"}g</span>
              <p className="text-[10px] text-[#666] uppercase">Grasas</p>
            </div>
          </div>
          
          {/* Progress bar for flexible mode - IMPROVED DAILY SUMMARY */}
          {dietMode !== "traditional" && todayMeals && (
            <div className="mt-3 pt-3 border-t border-[#2A2A2A]">
              {/* Summary Row */}
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-[#888]">
                  Hoy: <span className="text-white font-bold">{Math.round(todayMeals.daily_totals?.calories || 0)}</span> / {dailyTargets.calories} kcal
                </span>
                <span className={cn(
                  "font-medium",
                  Math.abs((todayMeals.daily_totals?.calories || 0) - dailyTargets.calories) <= dailyTargets.calories * 0.05 
                    ? "text-green-400" 
                    : "text-yellow-400"
                )}>
                  {todayMeals.completed_meals}/{todayMeals.total_meals} comidas
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="h-2 bg-[#2A2A2A] rounded-full overflow-hidden mb-2">
                <div 
                  className={cn(
                    "h-full transition-all",
                    Math.abs((todayMeals.daily_totals?.calories || 0) - dailyTargets.calories) <= dailyTargets.calories * 0.05 
                      ? "bg-green-500" 
                      : "bg-[#D4AF37]"
                  )}
                  style={{ width: `${Math.min((todayMeals.daily_totals?.calories / dailyTargets.calories) * 100, 100)}%` }}
                />
              </div>
              
              {/* Detailed Macro Summary - Only show when meals are selected */}
              {todayMeals.completed_meals > 0 && (
                <div className="grid grid-cols-4 gap-1 p-2 bg-[#1A1A1A] rounded-lg">
                  <div className="text-center">
                    <p className="text-[10px] text-[#666]">Cal</p>
                    <p className={cn(
                      "text-xs font-bold",
                      Math.abs((todayMeals.daily_totals?.calories || 0) - dailyTargets.calories) <= dailyTargets.calories * 0.05 
                        ? "text-green-400" : "text-white"
                    )}>
                      {Math.round(todayMeals.daily_totals?.calories || 0)}
                    </p>
                    <p className="text-[8px] text-[#666]">
                      {todayMeals.daily_remaining?.calories > 0 ? `-${Math.round(todayMeals.daily_remaining?.calories)}` : `+${Math.abs(Math.round(todayMeals.daily_remaining?.calories || 0))}`}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-[#666]">Prot</p>
                    <p className="text-xs font-bold text-red-400">
                      {Math.round(todayMeals.daily_totals?.protein || 0)}g
                    </p>
                    <p className="text-[8px] text-[#666]">
                      {todayMeals.daily_remaining?.protein > 0 ? `-${Math.round(todayMeals.daily_remaining?.protein)}` : `+${Math.abs(Math.round(todayMeals.daily_remaining?.protein || 0))}`}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-[#666]">Carbs</p>
                    <p className="text-xs font-bold text-yellow-400">
                      {Math.round(todayMeals.daily_totals?.carbs || 0)}g
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-[#666]">Grasas</p>
                    <p className="text-xs font-bold text-blue-400">
                      {Math.round(todayMeals.daily_totals?.fat || 0)}g
                    </p>
                  </div>
                </div>
              )}
              
              {/* Tolerance Warning */}
              {todayMeals.completed_meals === todayMeals.total_meals && 
               Math.abs((todayMeals.daily_totals?.calories || 0) - dailyTargets.calories) > dailyTargets.calories * 0.05 && (
                <div className="mt-2 p-2 bg-yellow-500/10 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-yellow-400" />
                  <p className="text-yellow-400 text-[10px]">
                    Ajuste aproximado: diferencia de {Math.abs(Math.round((todayMeals.daily_totals?.calories || 0) - dailyTargets.calories))} kcal
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ========== FLEXIBLE/HYBRID MODE VIEW ========== */}
      {(dietMode === "flexible" || dietMode === "hybrid") && flexiblePlan && (
        <div className="space-y-3 mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-green-400" />
            Comidas de Hoy
          </h3>
          
          {todayMeals?.meals?.map((meal) => {
            const slotConfig = flexiblePlan.meal_slots?.[meal.slot] || {};
            const isFlexibleSlot = dietMode === "flexible" || slotConfig.mode === "flexible";
            
            return (
              <Card 
                key={meal.slot}
                className={cn(
                  "bg-[#121212] border-[#2A2A2A]",
                  meal.has_selection && "border-green-500/30"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] flex items-center justify-center text-xl">
                        {getMealIcon(meal.slot_name)}
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">{meal.slot_name}</h4>
                        <div className="flex items-center gap-2">
                          <span className="text-[#D4AF37] text-xs font-bold">
                            {meal.target?.calories || slotConfig.targets?.calories || "~"} kcal
                          </span>
                          {meal.has_selection && (
                            <Badge className="bg-green-500/10 text-green-400 border-none text-[10px]">
                              <Check className="w-3 h-3 mr-1" />
                              Seleccionado
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {isFlexibleSlot && (
                      <Button
                        size="sm"
                        onClick={() => openRecipeSelector(meal.slot)}
                        className={cn(
                          meal.has_selection 
                            ? "bg-[#1A1A1A] text-[#888] hover:text-white"
                            : "bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                        )}
                      >
                        <Salad className="w-4 h-4 mr-1" />
                        {meal.has_selection ? "Cambiar" : "Elegir"}
                      </Button>
                    )}
                  </div>
                  
                  {/* Show selected recipe - FULL INLINE DISPLAY */}
                  {meal.has_selection && meal.selection && (
                    <div className="mt-3 p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
                      {/* Recipe Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="text-white font-bold text-base mb-1">
                            {meal.selection.recipe_name || "Receta seleccionada"}
                          </h5>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={cn(
                              "border-none text-[10px]",
                              meal.selection.fit_score >= 90 ? "bg-green-500/10 text-green-400" :
                              meal.selection.fit_score >= 70 ? "bg-yellow-500/10 text-yellow-400" :
                              "bg-red-500/10 text-red-400"
                            )}>
                              {Math.round(meal.selection.fit_score || 0)}% ajuste
                            </Badge>
                            {meal.selection.tiempo_preparacion && (
                              <span className="flex items-center gap-1 text-[#888] text-[10px]">
                                <Clock className="w-3 h-3" />
                                {meal.selection.tiempo_preparacion}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openRecipeSelector(meal.slot)}
                          className="text-[#888] hover:text-[#D4AF37] hover:bg-[#2A2A2A] h-8 px-2"
                          data-testid={`change-recipe-${meal.slot}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Cambiar
                        </Button>
                      </div>

                      {/* Macros Row */}
                      <div className="grid grid-cols-4 gap-2 p-2 bg-[#0B0B0B] rounded-lg mb-3">
                        <div className="text-center">
                          <p className="text-orange-400 font-bold text-sm">
                            {Math.round(
                              meal.selection.final_macros?.calories || 
                              meal.selection.final_macros?.calorias ||
                              meal.selection.calculated_macros?.calories ||
                              meal.selection.calculated_macros?.calorias ||
                              0
                            )}
                          </p>
                          <p className="text-[#666] text-[10px]">kcal</p>
                        </div>
                        <div className="text-center">
                          <p className="text-red-400 font-bold text-sm">
                            {Math.round(
                              meal.selection.final_macros?.protein || 
                              meal.selection.final_macros?.proteinas ||
                              meal.selection.calculated_macros?.protein ||
                              meal.selection.calculated_macros?.proteinas ||
                              0
                            )}g
                          </p>
                          <p className="text-[#666] text-[10px]">Prot</p>
                        </div>
                        <div className="text-center">
                          <p className="text-yellow-400 font-bold text-sm">
                            {Math.round(
                              meal.selection.final_macros?.carbs || 
                              meal.selection.final_macros?.carbohidratos ||
                              meal.selection.calculated_macros?.carbs ||
                              meal.selection.calculated_macros?.carbohidratos ||
                              0
                            )}g
                          </p>
                          <p className="text-[#666] text-[10px]">Carbs</p>
                        </div>
                        <div className="text-center">
                          <p className="text-blue-400 font-bold text-sm">
                            {Math.round(
                              meal.selection.final_macros?.fat || 
                              meal.selection.final_macros?.grasas ||
                              meal.selection.calculated_macros?.fat ||
                              meal.selection.calculated_macros?.grasas ||
                              0
                            )}g
                          </p>
                          <p className="text-[#666] text-[10px]">Grasas</p>
                        </div>
                      </div>

                      {/* Ingredients List */}
                      {meal.selection.adjusted_ingredients && meal.selection.adjusted_ingredients.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-[#888] text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Apple className="w-3 h-3" />
                            Ingredientes
                          </h6>
                          <div className="space-y-1.5">
                            {meal.selection.adjusted_ingredients
                              .map((ing, idx) => {
                                // ROBUST: Extract name from ALL possible fields
                                // Priority order matches what backend might send
                                const rawName = (
                                  ing.food_name ||
                                  ing.original_name ||
                                  ing.nombre ||
                                  ing.name ||
                                  ing.ingredient ||
                                  ing.alimento ||
                                  (typeof ing === 'string' ? ing : '')
                                );
                                
                                // Clean the name - remove any leading numbers/units
                                const ingredientName = rawName
                                  ? rawName.toString().replace(/^\d+\s*(g|ml|kg|l|oz)?\s*/i, '').trim()
                                  : '';
                                
                                // ROBUST: Extract amount from ALL possible fields
                                const amount = Math.round(
                                  ing.adjusted_amount ||
                                  ing.base_amount ||
                                  ing.cantidad ||
                                  ing.quantity ||
                                  ing.amount ||
                                  0
                                );
                                
                                // Get unit with more fallbacks
                                const unit = ing.unidad || ing.unit || ing.measure || 'g';
                                
                                // STRICT RULE: NO NAME = NO RENDER
                                // Never show just numbers without ingredient name
                                if (!ingredientName || ingredientName.length < 2) {
                                  return null; // Skip completely - don't render numbers alone
                                }
                                
                                return { ingredientName, amount, unit, idx };
                              })
                              .filter(Boolean) // Remove nulls
                              .map(({ ingredientName, amount, unit, idx }) => (
                                <div 
                                  key={idx}
                                  className="flex items-center justify-between py-1.5 px-2 bg-[#0B0B0B] rounded-lg"
                                >
                                  <span className="text-white text-sm">
                                    {ingredientName}
                                  </span>
                                  <span className="text-[#D4AF37] font-bold text-sm whitespace-nowrap">
                                    {amount > 0 ? `${amount} ${unit}` : '---'}
                                  </span>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )}

                      {/* Preparation Steps */}
                      {meal.selection.preparacion && meal.selection.preparacion.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-[#888] text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                            <ChefHat className="w-3 h-3" />
                            Preparación
                          </h6>
                          <ol className="space-y-2">
                            {meal.selection.preparacion.map((step, idx) => (
                              <li key={idx} className="flex gap-2 text-sm">
                                <span className="w-5 h-5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                  {idx + 1}
                                </span>
                                <span className="text-[#ccc]">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {/* Tips GOATGYM */}
                      {meal.selection.tips_goatgym && (
                        <div className="p-2 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/20">
                          <p className="text-[#D4AF37] text-xs flex items-start gap-2">
                            <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            {meal.selection.tips_goatgym}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ========== TRADITIONAL MODE VIEW ========== */}
      {(dietMode === "traditional" || dietMode === "hybrid") && diet && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 bg-[#1A1A1A] p-1 rounded-xl mb-4">
            <TabsTrigger value="comidas" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
              <Utensils className="w-4 h-4" />
            </TabsTrigger>
            <TabsTrigger value="info" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
              <Info className="w-4 h-4" />
            </TabsTrigger>
            <TabsTrigger value="supps" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
              <Pill className="w-4 h-4" />
            </TabsTrigger>
            <TabsTrigger value="ajustes" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
              <RefreshCw className="w-4 h-4" />
            </TabsTrigger>
          </TabsList>

          {/* COMIDAS TAB */}
          <TabsContent value="comidas" className="space-y-3">
            {diet?.meals?.map((meal, index) => {
              const isExpanded = expandedMeal === index;
              
              return (
                <Card 
                  key={meal.name ? `meal-${meal.name}-${index}` : `meal-${index}`}
                  className={cn(
                    "bg-[#121212] border-[#2A2A2A] transition-all overflow-hidden",
                    isExpanded && "border-[#D4AF37]/30"
                  )}
                >
                  <CardContent className="p-0">
                    <button
                      onClick={() => toggleMeal(index)}
                      className="w-full p-4 flex items-center gap-4 text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#1A1A1A] flex items-center justify-center text-2xl flex-shrink-0">
                        {getMealIcon(meal.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-bold text-base">{meal.name}</h3>
                        <p className="text-[#666] text-sm flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {meal.time}
                        </p>
                      </div>
                      <div className={cn(
                        "p-2 rounded-lg transition-colors",
                        isExpanded ? "bg-[#D4AF37]/20" : "bg-[#1A1A1A]"
                      )}>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-[#D4AF37]" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[#666]" />
                        )}
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 border-t border-[#2A2A2A] space-y-3">
                        <div className="mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 text-xs font-bold flex items-center justify-center">A</span>
                            <span className="text-[#888] text-xs uppercase tracking-wider">Opción A</span>
                          </div>
                          <p className="text-white text-sm pl-8">{meal.option_a}</p>
                        </div>
                        
                        {meal.option_b && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center">B</span>
                              <span className="text-[#888] text-xs uppercase tracking-wider">Opción B</span>
                            </div>
                            <p className="text-white text-sm pl-8">{meal.option_b}</p>
                          </div>
                        )}
                        
                        {meal.notes && (
                          <div className="flex items-start gap-2 mt-3 p-3 bg-[#1A1A1A] rounded-lg">
                            <Info className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                            <p className="text-[#888] text-xs">{meal.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            <div className="p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
              <p className="text-[#888] text-sm text-center">
                <span className="text-[#D4AF37] font-semibold">Elige una opción</span> por cada comida. 
                Ambas tienen los mismos macros aproximados.
              </p>
            </div>
          </TabsContent>

          {/* INFO TAB */}
          <TabsContent value="info" className="space-y-4">
            {diet.sustituciones && (
              <Card className="bg-[#121212] border-[#2A2A2A]">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
                    <RefreshCw className="w-4 h-4 text-blue-400" />
                    Sustituciones Permitidas
                  </h3>
                  <p className="text-[#ccc] text-sm whitespace-pre-line">{diet.sustituciones}</p>
                </CardContent>
              </Card>
            )}

            {diet.notas_goatgym && (
              <Card className="bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 border-[#D4AF37]/30">
                <CardContent className="p-4">
                  <h3 className="text-[#D4AF37] font-semibold flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4" />
                    Notas GOATGYM
                  </h3>
                  <p className="text-white text-sm whitespace-pre-line">{diet.notas_goatgym}</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-[#121212] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
                  <Beef className="w-4 h-4 text-red-400" />
                  Recuerda
                </h3>
                <ul className="text-[#888] text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF37]">•</span>
                    Pesos de alimentos siempre en CRUDO
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF37]">•</span>
                    Verduras sin almidón: sin límite
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF37]">•</span>
                    Bebe mínimo 2-2.5L de agua al día
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#D4AF37]">•</span>
                    Respeta horarios lo máximo posible
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUPPS TAB */}
          <TabsContent value="supps" className="space-y-4">
            {diet.suplementacion ? (
              <Card className="bg-[#121212] border-[#2A2A2A]">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
                    <Pill className="w-4 h-4 text-purple-400" />
                    Suplementación Recomendada
                  </h3>
                  <p className="text-[#ccc] text-sm whitespace-pre-line">{diet.suplementacion}</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#121212] border-[#2A2A2A]">
                <CardContent className="p-8 text-center">
                  <Pill className="w-12 h-12 text-[#333] mx-auto mb-3" />
                  <p className="text-[#666]">Sin suplementación específica asignada</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <p className="text-[#666] text-xs text-center">
                  Los suplementos son opcionales. La base siempre es la alimentación.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AJUSTES TAB */}
          <TabsContent value="ajustes" className="space-y-4">
            {diet.ajustes_estancamiento ? (
              <Card className="bg-[#121212] border-[#2A2A2A]">
                <CardContent className="p-4">
                  <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    Si Te Estancas...
                  </h3>
                  <p className="text-[#ccc] text-sm whitespace-pre-line">{diet.ajustes_estancamiento}</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#121212] border-[#2A2A2A]">
                <CardContent className="p-8 text-center">
                  <RefreshCw className="w-12 h-12 text-[#333] mx-auto mb-3" />
                  <p className="text-[#666]">Sin ajustes específicos configurados</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardContent className="p-4">
                <p className="text-[#888] text-sm text-center">
                  <span className="text-[#D4AF37] font-semibold">¿Llevas 2+ semanas estancado?</span>
                  <br />
                  Consulta con tu coach antes de hacer cambios.
                </p>
                <Button
                  onClick={() => window.open("https://wa.me/34641099731?text=Hola!%20Llevo%20tiempo%20estancado%20y%20necesito%20revisar%20mi%20dieta", "_blank")}
                  variant="outline"
                  className="w-full mt-3 border-green-500/30 text-green-400 hover:bg-green-500/10"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contactar Coach
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Recipe Selector Dialog */}
      <Dialog open={showRecipeDialog} onOpenChange={setShowRecipeDialog}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
              <ChefHat className="w-5 h-5" />
              Elegir Receta
            </DialogTitle>
          </DialogHeader>

          {!calculatedRecipe ? (
            <div className="space-y-3">
              {loadingRecipes ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : availableRecipes.length > 0 ? (
                availableRecipes.map((recipe) => (
                  <Card 
                    key={recipe.id}
                    className="bg-[#1A1A1A] border-[#2A2A2A] cursor-pointer hover:border-[#D4AF37]/30 transition-all overflow-hidden"
                    onClick={() => {
                      const slotTargets = flexiblePlan?.meal_slots?.[selectedMealSlot]?.targets;
                      if (slotTargets) {
                        calculateRecipe(recipe.id, slotTargets);
                      }
                    }}
                  >
                    <div className="flex">
                      {/* Recipe Image */}
                      <div className="w-20 h-20 flex-shrink-0">
                        <img 
                          src={getRecipeImage(recipe)} 
                          alt={recipe.nombre}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <CardContent className="p-3 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-white font-medium text-sm line-clamp-1">{recipe.nombre}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[#888] text-xs">
                                {recipe.macros?.calorias || "~"} kcal
                              </span>
                              {recipe.compatibility_score && (
                                <Badge className={cn(
                                  "border-none text-[10px]",
                                  recipe.compatibility_score >= 80 ? "bg-green-500/10 text-green-400" :
                                  recipe.compatibility_score >= 60 ? "bg-yellow-500/10 text-yellow-400" :
                                  "bg-red-500/10 text-red-400"
                                )}>
                                  {recipe.compatibility_score}%
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Calculator className="w-4 h-4 text-[#666]" />
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))
              ) : (
                <p className="text-[#666] text-center py-8">No hay recetas disponibles</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {calculatingRecipe ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="p-3 bg-[#1A1A1A] rounded-lg">
                    <h4 className="text-white font-semibold mb-2">{calculatedRecipe.recipe_name}</h4>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={cn(
                        "border-none text-xs",
                        calculatedRecipe.fit_score >= 90 ? "bg-green-500/10 text-green-400" :
                        calculatedRecipe.fit_score >= 70 ? "bg-yellow-500/10 text-yellow-400" :
                        "bg-red-500/10 text-red-400"
                      )}>
                        {Math.round(calculatedRecipe.fit_score)}% ajuste
                      </Badge>
                      {calculatedRecipe.is_approximate && (
                        <Badge className="bg-yellow-500/10 text-yellow-400 border-none text-xs">
                          Ajuste aproximado
                        </Badge>
                      )}
                    </div>
                    {calculatedRecipe.approximate_reason && (
                      <p className="text-yellow-400 text-[10px] mt-2">
                        ⚠️ {calculatedRecipe.approximate_reason}
                      </p>
                    )}
                  </div>

                  {/* Target vs Final Comparison */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-[#0B0B0B] rounded-lg">
                      <p className="text-[#666] text-[10px] mb-1">OBJETIVO</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-white font-bold">{calculatedRecipe.target_macros?.calories}</span>
                        <span className="text-[#666] text-[10px]">kcal</span>
                      </div>
                      <p className="text-[#888] text-[10px]">
                        {calculatedRecipe.target_macros?.protein}P / {calculatedRecipe.target_macros?.carbs}C / {calculatedRecipe.target_macros?.fat}G
                      </p>
                    </div>
                    <div className={cn(
                      "p-2 rounded-lg",
                      calculatedRecipe.within_tolerance ? "bg-green-500/10" : "bg-yellow-500/10"
                    )}>
                      <p className="text-[#666] text-[10px] mb-1">RESULTADO</p>
                      <div className="flex items-baseline gap-1">
                        <span className={cn(
                          "font-bold",
                          calculatedRecipe.within_tolerance ? "text-green-400" : "text-yellow-400"
                        )}>
                          {Math.round(calculatedRecipe.final_macros?.calories || 0)}
                        </span>
                        <span className="text-[#666] text-[10px]">kcal</span>
                        <span className={cn(
                          "text-[10px]",
                          calculatedRecipe.deviation?.calories > 0 ? "text-red-400" : "text-green-400"
                        )}>
                          ({calculatedRecipe.deviation?.calories > 0 ? "+" : ""}{Math.round(calculatedRecipe.deviation?.calories || 0)})
                        </span>
                      </div>
                      <p className="text-[#888] text-[10px]">
                        {Math.round(calculatedRecipe.final_macros?.protein || 0)}P / {Math.round(calculatedRecipe.final_macros?.carbs || 0)}C / {Math.round(calculatedRecipe.final_macros?.fat || 0)}G
                      </p>
                    </div>
                  </div>

                  {/* Ingredients */}
                  <div>
                    <h5 className="text-[#888] text-xs font-semibold mb-2">INGREDIENTES AJUSTADOS</h5>
                    <div className="space-y-2 max-h-[150px] overflow-y-auto">
                      {calculatedRecipe.adjusted_ingredients
                        ?.map((ing, idx) => {
                          // ROBUST: Extract name from ALL possible fields
                          const rawName = (
                            ing.food_name ||
                            ing.original_name ||
                            ing.nombre ||
                            ing.name ||
                            ing.ingredient ||
                            ''
                          );
                          
                          // Clean name
                          const ingredientName = rawName
                            ? rawName.toString().replace(/^\d+\s*(g|ml|kg|l|oz)?\s*/i, '').trim()
                            : '';
                          
                          // STRICT: No name = don't render
                          if (!ingredientName || ingredientName.length < 2) {
                            return null;
                          }
                          
                          const amount = Math.round(ing.adjusted_amount || ing.base_amount || ing.cantidad || 0);
                          const unit = ing.unidad || ing.unit || 'g';
                          
                          return { ingredientName, amount, unit, idx };
                        })
                        .filter(Boolean)
                        .map(({ ingredientName, amount, unit, idx }) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-[#1A1A1A] rounded">
                            <span className="text-white text-sm">{ingredientName}</span>
                            <span className="text-[#D4AF37] font-bold text-sm">
                              {amount > 0 ? `${amount}${unit}` : '---'}
                            </span>
                          </div>
                        ))
                      }
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setCalculatedRecipe(null)}
                      className="flex-1 border-[#2A2A2A] text-white"
                    >
                      Cambiar receta
                    </Button>
                    <Button
                      onClick={saveRecipeSelection}
                      className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Confirmar
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DietaPage;
